import type { EventCategory, ParsedBookableEvent } from './types'

function flexibleBookingOccurrenceKey(event: ParsedBookableEvent): string {
    return `${event.ruleId}|${event.startDatetime.startOf('minute').toISO()}`
}

function inferRuleCategoryIdsFromSiblingSlots<
    T extends { ruleId: number; categoryId: number; ruleCategoryIds?: number[] }
>(events: T[], occurrenceKey: (event: T) => string | null): T[] {
    const groups = new Map<string, T[]>()
    for (const event of events) {
        const key = occurrenceKey(event)
        if (!key) {
            continue
        }
        const group = groups.get(key)
        if (group) {
            group.push(event)
        } else {
            groups.set(key, [event])
        }
    }

    return events.map((event) => {
        const key = occurrenceKey(event)
        if (!key) {
            return event
        }
        const siblings = groups.get(key)
        if (!siblings || siblings.length < 2) {
            return event
        }

        const ids = new Set<number>()
        for (const sibling of siblings) {
            ids.add(sibling.categoryId)
            if (sibling.ruleCategoryIds) {
                for (const id of sibling.ruleCategoryIds) {
                    ids.add(id)
                }
            }
        }

        if (ids.size < 2) {
            return event
        }

        const inferred = [...ids].sort((a, b) => a - b)
        return { ...event, ruleCategoryIds: inferred }
    })
}

export function inferRuleCategoryIdsFromParsedBookableEvents(events: ParsedBookableEvent[]): ParsedBookableEvent[] {
    return inferRuleCategoryIdsFromSiblingSlots(events, (event) => {
        if (!event.startDatetime.isValid) {
            return null
        }
        return flexibleBookingOccurrenceKey(event)
    })
}

export function normalizeFlexibleBookingEvents(
    events: ParsedBookableEvent[],
    categories: Record<number, EventCategory>,
    selectedCategoryId: number | null
): ParsedBookableEvent[] {
    if (!selectedCategoryId || events.length === 0) {
        return events
    }

    const mergedFlexibleIdsByKey = new Map<string, Set<number>>()
    for (const event of events) {
        const key = flexibleBookingOccurrenceKey(event)
        let ids = mergedFlexibleIdsByKey.get(key)
        if (!ids) {
            ids = new Set<number>()
            mergedFlexibleIdsByKey.set(key, ids)
        }
        ids.add(event.categoryId)
        if (event.ruleCategoryIds) {
            for (const id of event.ruleCategoryIds) {
                ids.add(id)
            }
        }
    }

    const remapped = events.map((event) => {
        const key = flexibleBookingOccurrenceKey(event)
        const flexibleIds = mergedFlexibleIdsByKey.get(key) ?? new Set<number>()

        if (flexibleIds.size < 2 || !flexibleIds.has(selectedCategoryId)) {
            return event
        }

        if (event.categoryId === selectedCategoryId) {
            return event
        }

        const category = categories[selectedCategoryId]
        if (!category) {
            return event
        }

        const durationMinutes = Number(category.duration)
        if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) {
            return event
        }

        const endDatetime = event.startDatetime.plus({ minutes: durationMinutes })

        return {
            ...event,
            categoryId: selectedCategoryId,
            end: endDatetime.toISO(),
            endDatetime
        }
    })

    const deduped = new Map<string, ParsedBookableEvent>()
    for (const event of remapped) {
        deduped.set(`${flexibleBookingOccurrenceKey(event)}|${event.categoryId}`, event)
    }

    return [...deduped.values()]
}

/**
 * Collapses bookable slots that are identical from the client's perspective:
 * same provider, same start minute and same (resolved) category. Overlapping
 * event rules, flexible rules that emit a category variant more than once, and
 * management/sub-user setups can otherwise surface the exact same slot multiple
 * times. A provider can never offer two distinct appointments of the same
 * category at the same time, so keeping the first occurrence is always safe.
 */
export function dedupeIdenticalBookableSlots(events: ParsedBookableEvent[]): ParsedBookableEvent[] {
    const seen = new Set<string>()
    const result: ParsedBookableEvent[] = []
    for (const event of events) {
        const therapistId = event.therapist?.id ?? 0
        const startKey = event.startDatetime.isValid ? event.startDatetime.startOf('minute').toISO() : event.start
        const key = `${therapistId}|${startKey}|${event.categoryId}`
        if (!seen.has(key)) {
            seen.add(key)
            result.push(event)
        }
    }
    return result
}
